import { useState, useEffect, useRef, useMemo } from 'react';
import type { IAssistantScriptDialogProps } from '@shared/interfaces/assistantScripts';
import { PollyLanguage } from '@shared/interfaces/polly';
import { ModalThemeProvider } from '@shared/components/AssistantEditorModals/context';
import { DEFAULT_SCRIPT_LANGUAGES } from './constants';
import LanguagePickerDialog from './LanguagePickerDialog';
import TranslateConfirmDialog from './TranslateConfirmDialog';
import LanguageTabs from './LanguageTabs';
import CreateWizard from './CreateWizard';
import EditSummary from './EditSummary';
import { splitTranslationTargets } from './translateTargets';
import useAudioPreview from './hooks/useAudioPreview';
import useScriptSteps from './hooks/useScriptSteps';
import type { ScriptDialogState } from './types';

const AssistantScriptDialog = ({
  open,
  assistantScript,
  trigger,
  defaultTriggerType,
  title,
  engine,
  voiceId,
  baseLanguage,
  languages,
  voicesByLanguage,
  mapName,
  poiId,
  brandingId,
  paperRef,
  modalTheme = 'light',
  userFirstName,
  onGenerateAudio,
  onTranslate,
  onSave,
  onClose,
}: IAssistantScriptDialogProps) => {
  const effectiveBaseLanguage = baseLanguage ?? PollyLanguage.ENGLISH;
  const scriptLanguages = useMemo(() => {
    const defaultAdditional = DEFAULT_SCRIPT_LANGUAGES.filter(
      (l) => l !== PollyLanguage.ENGLISH,
    );
    const additional = (languages ?? defaultAdditional).filter(
      (l) => l !== effectiveBaseLanguage,
    );
    return [effectiveBaseLanguage, ...additional];
  }, [effectiveBaseLanguage, languages]);

  const scriptStepsEndRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const stepsHeaderRef = useRef<HTMLDivElement>(null);

  const [isAddStepFixed, setIsAddStepFixed] = useState(false);

  const [activeLanguage, setActiveLanguage] = useState<PollyLanguage>(
    effectiveBaseLanguage,
  );
  const [translateMenuAnchor, setTranslateMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [pickedLangs, setPickedLangs] = useState<PollyLanguage[]>([]);
  // When a translate request targets languages that already have translations,
  // we pause and ask the user whether to overwrite all or fill only the empty
  // ones (see requestTranslate).
  const [translateConfirm, setTranslateConfirm] = useState<{
    targetLangs: PollyLanguage[];
    existing: PollyLanguage[];
    empty: PollyLanguage[];
    origin: 'all' | 'picked';
  } | null>(null);

  const {
    scriptSteps,
    stepExpanded,
    stepUserInputModes,
    stepsModified,
    isSubmitting,
    isTranslating,
    triggerType,
    setTriggerType,
    uiClickButtonLabel,
    setUiClickButtonLabel,
    uiClickButtonLabelTranslations,
    uiClickButtonLabelStale,
    updateUiClickButtonLabelTranslation,
    interruptMode,
    setInterruptMode,
    bridgeRegionGaps,
    setBridgeRegionGaps,
    allowSkipDialog,
    setAllowSkipDialog,
    playbackPolicy,
    setPlaybackPolicy,
    skipFirstSession,
    setSkipFirstSession,
    formik,
    addNewStep,
    removeStep,
    updateStep,
    updateStepTranslation,
    moveStepUp,
    moveStepDown,
    toggleStepExpanded,
    handleUserInputModeChange,
    handleTranslateTo,
    handleTranslateButtonLabelTo,
  } = useScriptSteps({
    assistantScript,
    trigger,
    defaultTriggerType,
    title,
    mapName,
    brandingId,
    poiId,
    scriptStepsEndRef,
    onSave,
    onTranslate,
  });

  const { audioPlaybackKey, isAudioPlaying, playAudio, stopAudio } =
    useAudioPreview({
      activeLanguage,
      effectiveBaseLanguage,
      engine,
      voiceId,
      voicesByLanguage,
      onGenerateAudio,
    });

  // The dialog stays mounted when hidden (MUI Dialog), so closing it only flips
  // `open`. Stop any preview audio when it closes so it can't play in the
  // background.
  useEffect(() => {
    if (!open) {
      stopAudio();
    }
  }, [open, stopAudio]);

  const nonBaseLangs = useMemo(
    () => scriptLanguages.slice(1),
    [scriptLanguages],
  );

  useEffect(() => {
    if (!open) {
      setIsAddStepFixed(false);
      return;
    }

    const handleScroll = () => {
      const sentinel = stepsHeaderRef.current;
      const container = modalBodyRef.current;

      if (!sentinel || !container) {
        return;
      }

      const sentinelRect = sentinel.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const shouldBeFixed = sentinelRect.bottom < containerRect.top;

      setIsAddStepFixed(shouldBeFixed);
    };

    let container: HTMLDivElement | null = null;

    const raf = requestAnimationFrame(() => {
      container = modalBodyRef.current;

      if (!container) {
        return;
      }

      container.addEventListener('scroll', handleScroll);
    });

    return () => {
      cancelAnimationFrame(raf);
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  const hasChanges = formik.dirty || stepsModified;

  // Entry point for both "Auto-translate all" and the language picker. If none
  // of the target languages already have a translation we translate straight
  // away; otherwise we open the overwrite/empty-only prompt.
  const requestTranslate = (
    targetLangs: PollyLanguage[],
    origin: 'all' | 'picked',
  ) => {
    if (!targetLangs.length) {
      return;
    }

    const { existing, empty, needsPrompt } = splitTranslationTargets(
      'steps',
      targetLangs,
      scriptSteps,
      uiClickButtonLabelTranslations,
    );

    if (!needsPrompt) {
      handleTranslateTo(targetLangs);
      return;
    }

    setTranslateConfirm({ targetLangs, existing, empty, origin });
  };

  const stepsLanguageTabs = (
    <LanguageTabs
      scriptLanguages={scriptLanguages}
      effectiveBaseLanguage={effectiveBaseLanguage}
      activeLanguage={activeLanguage}
      scriptSteps={scriptSteps}
      isTranslating={isTranslating}
      translateMenuAnchor={translateMenuAnchor}
      stepsFormRef={stepsHeaderRef}
      audioPlaybackKey={audioPlaybackKey}
      isAudioPlaying={isAudioPlaying}
      userFirstName={userFirstName}
      mapName={mapName}
      onActiveLanguageChange={setActiveLanguage}
      onTranslateAll={() => requestTranslate(nonBaseLangs, 'all')}
      onTranslateMenuOpen={setTranslateMenuAnchor}
      onTranslateMenuClose={() => setTranslateMenuAnchor(null)}
      onPickLanguages={() => {
        setPickedLangs([]);
        setLangPickerOpen(true);
      }}
      onPlay={playAudio}
      onStop={stopAudio}
    />
  );

  const state: ScriptDialogState = {
    assistantScript,
    title,
    mapName,
    userFirstName,
    modalTheme,

    triggerType,
    setTriggerType,
    uiClickButtonLabel,
    setUiClickButtonLabel,
    uiClickButtonLabelTranslations,
    uiClickButtonLabelStale,
    updateUiClickButtonLabelTranslation,
    onTranslateUiClickButtonLabel: handleTranslateButtonLabelTo,
    isTranslating,

    interruptMode,
    setInterruptMode,
    bridgeRegionGaps,
    setBridgeRegionGaps,
    allowSkipDialog,
    setAllowSkipDialog,
    playbackPolicy,
    setPlaybackPolicy,
    skipFirstSession,
    setSkipFirstSession,

    scriptSteps,
    stepExpanded,
    stepUserInputModes,
    toggleStepExpanded,
    moveStepUp,
    moveStepDown,
    updateStep,
    updateStepTranslation,
    handleUserInputModeChange,
    removeStep,
    addNewStep,

    audioPlaybackKey,
    isAudioPlaying,
    playAudio,
    stopAudio,

    activeLanguage,
    setActiveLanguage,
    effectiveBaseLanguage,
    nonBaseLangs,
    stepsLanguageTabs,

    isAddStepFixed,
    modalBodyRef,
    stepsHeaderRef,
    scriptStepsEndRef,

    formik,
    isSubmitting,
    hasChanges,
  };

  return (
    <ModalThemeProvider value={modalTheme}>
      <>
        {!assistantScript?.scriptId ? (
          <CreateWizard
            open={open}
            state={state}
            paperRef={paperRef}
            onClose={onClose}
          />
        ) : (
          <EditSummary
            open={open}
            state={state}
            paperRef={paperRef}
            onClose={onClose}
          />
        )}

        <LanguagePickerDialog
          open={langPickerOpen}
          nonBaseLangs={nonBaseLangs}
          pickedLangs={pickedLangs}
          isTranslating={isTranslating}
          onPickedLangsChange={setPickedLangs}
          onClose={() => setLangPickerOpen(false)}
          onTranslate={(langs) => requestTranslate(langs, 'picked')}
        />

        <TranslateConfirmDialog
          open={!!translateConfirm}
          existing={translateConfirm?.existing ?? []}
          empty={translateConfirm?.empty ?? []}
          targetLangs={translateConfirm?.targetLangs ?? []}
          origin={translateConfirm?.origin ?? 'all'}
          isTranslating={isTranslating}
          onOverwriteAll={() => {
            if (translateConfirm) {
              handleTranslateTo(translateConfirm.targetLangs);
            }
            setTranslateConfirm(null);
          }}
          onOnlyEmpty={() => {
            if (translateConfirm) {
              handleTranslateTo(translateConfirm.empty);
            }
            setTranslateConfirm(null);
          }}
          onClose={() => setTranslateConfirm(null)}
        />
      </>
    </ModalThemeProvider>
  );
};

export default AssistantScriptDialog;
